import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import { FiHome, FiMail, FiPhone, FiMapPin, FiClock, FiSend } from "react-icons/fi";
import { ContactUsForm } from "../services/website.services";
import {
  SWAL_SETTINGS,
  INPUT_LENGTH_100,
  INPUT_LENGTH_500,
  INPUT_LENGTH_30,
} from "../../../utils/Constants";
import {
  DT,
  globalLoader,
  handleServerValidations,
  setTitle,
} from "../../../utils/commonfunction";

const WebsiteContactUs = ({ id }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [globalData, setGlobalData] = useState({});
  const getGlobalData = useSelector((state) => state.globalData?.data);

  useEffect(() => {
    setTitle("Contact Us | Astro Adi");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (getGlobalData) {
      try {
        const globalSettingData = typeof getGlobalData === "string" ? JSON.parse(getGlobalData) : getGlobalData;
        setGlobalData(globalSettingData);
      } catch (e) {
        console.error("Error parsing global data:", e);
      }
    }
  }, [getGlobalData]);

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .max(INPUT_LENGTH_30, DT(t("err_exceed_max_length"), [INPUT_LENGTH_30]))
      .required(t("label_name_error") || "Name is required"),
    email: Yup.string()
      .trim()
      .email(t("label_email_invalid_format_error") || "Invalid email address")
      .required(t("err_email_is_required") || "Email is required"),
    subject: Yup.string()
      .trim()
      .max(INPUT_LENGTH_100, DT(t("err_exceed_max_length"), [INPUT_LENGTH_100]))
      .required(t("label_subject_error") || "Subject is required"),
    message: Yup.string()
      .trim()
      .max(INPUT_LENGTH_500, DT(t("err_exceed_max_length"), [INPUT_LENGTH_500]))
      .required(t("err_message_is_required") || "Message is required"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
    validateOnBlur: false,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      globalLoader(true);
      let formData = new FormData();
      formData.append("name", values.name);
      formData.append("email", values.email);
      formData.append("subject", values.subject);
      formData.append("message", values.message);

      ContactUsForm(formData)
        .then((response) => {
          globalLoader(false);
          setSubmitting(false);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message || "Thank you! Your message has been sent successfully.",
              ...SWAL_SETTINGS,
            });
            resetForm();
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((error) => {
          globalLoader(false);
          setSubmitting(false);
          console.error("Contact Form Error:", error);
          Swal.fire({
            icon: "error",
            text: "Failed to send message. Please try again later.",
            ...SWAL_SETTINGS,
          });
        });
    },
  });

  return (
    <div id={id} className="contact-page-wrapper bg-white">
      {/* ===== Hero Banner Section ===== */}
      <section className="relative bg-[url('../../images/banner.png')] bg-cover bg-center !pt-[78px] md:!pt-[88px] !pb-[16px] md:!pb-[22px] overflow-hidden text-center">
        {/* Dark overlay for contrast */}
        <div className="absolute inset-0 bg-black/65 z-[1]"></div>

        <div className="container relative z-[2] mx-auto px-4">
          <div className="max-w-[700px] mx-auto flex flex-col items-center gap-1.5">
            {/* Golden Badge */}
            <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-yellow-500/15 border border-yellow-400/40 text-yellow-400 text-[11px] md:text-xs font-semibold tracking-wider uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse"></span>
              GET IN TOUCH
            </div>

            {/* Main Page Title */}
            <h1 className="web-h1 text-white text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight m-0 drop-shadow-md">
              Contact Us
            </h1>

            {/* Breadcrumb Navigation */}
            <nav className="inline-flex items-center gap-2 bg-black/40 backdrop-blur-md px-3.5 py-1 rounded-full border border-white/10 text-xs text-gray-300 mt-0.5">
              <Link
                to="/"
                className="text-gray-300 hover:text-yellow-400 no-underline transition-colors flex items-center gap-1"
              >
                <FiHome className="text-xs" />
                <span>Home</span>
              </Link>
              <span className="text-gray-500">/</span>
              <span className="text-yellow-400 font-medium">Contact Us</span>
            </nav>
          </div>
        </div>
      </section>

      {/* ===== Content Section with Contact Info & Form ===== */}
      <section className="pt-4 md:pt-6 pb-12 md:pb-16 bg-gray-50/70 relative overflow-hidden">
        {/* Decorative background shapes */}
        <div className="absolute top-[10%] right-0 hidden 2xl:block opacity-40 pointer-events-none">
          <img src="/images/shape1.png" alt="" />
        </div>
        <div className="absolute bottom-[10%] left-0 hidden 2xl:block opacity-40 pointer-events-none">
          <img src="/images/shape2.png" alt="" />
        </div>

        <div className="container max-w-6xl mx-auto px-4 sm:px-6 relative z-[2]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Left Column: Contact Info Cards */}
            <div className="lg:col-span-5 flex flex-col gap-6">
              <div>
                <span className="inline-flex items-center gap-2 tracking-[.2em] text-xs font-bold text-yellow-600 uppercase mb-2">
                  <span className="w-6 h-[2px] bg-yellow-500 inline-block"></span>
                  REACH OUT
                </span>
                <h2 className="web-h2 text-zinc-900 text-2xl sm:text-3xl font-bold mt-1">
                  We're Here to Guide You
                </h2>
                <p className="text-zinc-600 text-base leading-relaxed mt-2">
                  Have questions about consultations, remedies, or your orders? Contact our support team for prompt assistance.
                </p>
              </div>

              {/* Info Cards */}
              <div className="flex flex-col gap-4">
                {/* Email Card */}
                <div className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-200/60 flex items-center justify-center text-yellow-600 text-xl flex-shrink-0">
                    <FiMail />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-wider text-gray-400 font-semibold block">Email Support</span>
                    <a
                      href={`mailto:${globalData?.contact_email || "support@astroadi.com"}`}
                      className="text-gray-900 font-medium text-base hover:text-yellow-600 transition-colors no-underline block mt-0.5"
                    >
                      {globalData?.contact_email || "support@astroadi.com"}
                    </a>
                  </div>
                </div>

                {/* Phone Card */}
                <div className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-200/60 flex items-center justify-center text-yellow-600 text-xl flex-shrink-0">
                    <FiPhone />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-wider text-gray-400 font-semibold block">Phone Number</span>
                    <a
                      href={`tel:${globalData?.phone_number || "+919876543210"}`}
                      className="text-gray-900 font-medium text-base hover:text-yellow-600 transition-colors no-underline block mt-0.5"
                    >
                      {globalData?.phone_number || "+91 98765 43210"}
                    </a>
                  </div>
                </div>

                {/* Address Card */}
                {globalData?.address && (
                  <div className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-200/60 flex items-center justify-center text-yellow-600 text-xl flex-shrink-0">
                      <FiMapPin />
                    </div>
                    <div>
                      <span className="text-xs uppercase tracking-wider text-gray-400 font-semibold block">Location</span>
                      <p className="text-gray-900 font-medium text-base m-0 mt-0.5">
                        {globalData.address}
                      </p>
                    </div>
                  </div>
                )}

                {/* Working Hours Card */}
                <div className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-200/60 flex items-center justify-center text-yellow-600 text-xl flex-shrink-0">
                    <FiClock />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-wider text-gray-400 font-semibold block">Hours</span>
                    <p className="text-gray-900 font-medium text-base m-0 mt-0.5">
                      Monday to Sunday: 9:00 AM – 9:00 PM IST
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Us Form */}
            <div className="lg:col-span-7">
              <div className="bg-white rounded-3xl p-6 sm:p-10 shadow-xl shadow-gray-200/50 border border-gray-100">
                <div className="mb-6">
                  <h3 className="web-h3 text-gray-900 text-xl sm:text-2xl font-bold">
                    Send Us a Message
                  </h3>
                  <p className="text-gray-500 text-sm mt-1">
                    Fill out the form below and our team will get back to you within 24 hours.
                  </p>
                </div>

                <form onSubmit={formik.handleSubmit} className="space-y-5">
                  {/* Name Input */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Your Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Enter your name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full px-4 py-3 rounded-xl border ${
                        formik.touched.name && formik.errors.name
                          ? "border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-[#DEA916] focus:ring-yellow-100"
                      } focus:outline-none focus:ring-2 transition-all text-gray-900 text-base`}
                    />
                    {formik.touched.name && formik.errors.name && (
                      <span className="text-red-500 text-xs mt-1 block">{formik.errors.name}</span>
                    )}
                  </div>

                  {/* Email Input */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full px-4 py-3 rounded-xl border ${
                        formik.touched.email && formik.errors.email
                          ? "border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-[#DEA916] focus:ring-yellow-100"
                      } focus:outline-none focus:ring-2 transition-all text-gray-900 text-base`}
                    />
                    {formik.touched.email && formik.errors.email && (
                      <span className="text-red-500 text-xs mt-1 block">{formik.errors.email}</span>
                    )}
                  </div>

                  {/* Subject Input */}
                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="subject"
                      name="subject"
                      type="text"
                      placeholder="How can we help you?"
                      value={formik.values.subject}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full px-4 py-3 rounded-xl border ${
                        formik.touched.subject && formik.errors.subject
                          ? "border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-[#DEA916] focus:ring-yellow-100"
                      } focus:outline-none focus:ring-2 transition-all text-gray-900 text-base`}
                    />
                    {formik.touched.subject && formik.errors.subject && (
                      <span className="text-red-500 text-xs mt-1 block">{formik.errors.subject}</span>
                    )}
                  </div>

                  {/* Message Textarea */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      placeholder="Write your message or inquiry here..."
                      value={formik.values.message}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full px-4 py-3 rounded-xl border ${
                        formik.touched.message && formik.errors.message
                          ? "border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-[#DEA916] focus:ring-yellow-100"
                      } focus:outline-none focus:ring-2 transition-all text-gray-900 text-base resize-none`}
                    ></textarea>
                    {formik.touched.message && formik.errors.message && (
                      <span className="text-red-500 text-xs mt-1 block">{formik.errors.message}</span>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={formik.isSubmitting}
                      className="custom-btn custom-btn-primary w-full sm:w-auto !py-3.5 !px-8 flex items-center justify-center gap-2 text-base font-semibold shadow-md shadow-yellow-500/20"
                    >
                      <FiSend />
                      <span>{formik.isSubmitting ? "Sending Message..." : "Send Message"}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebsiteContactUs;
